import type { SetlistContext } from './client';
import { STOCK_VIDEO_CATEGORY_GROUPS } from './collections';
import {
  VIDEO_CATEGORY_GROUP_FIELDS,
  type VideoCategoryGroup,
} from './stock-video-types';
import { VECTOR_SEARCH_DEFAULTS } from './vector-search';

/**
 * Fetches video category groups similar to a given category using vector search.
 * Uses multi-search endpoint (POST) to avoid URL length limits with large embeddings.
 *
 * Note: This fetches GROUPS, not categories. The current category may belong to one
 * of these groups, which is fine - we exclude the current category when fetching
 * categories within each group (in getVideoCategoriesByGroup).
 */
export const getVideoCategoryGroups = async (
  context: SetlistContext,
  categoryEmbedding: number[],
  distanceThreshold = VECTOR_SEARCH_DEFAULTS.GROUP.DISTANCE_THRESHOLD,
  k = VECTOR_SEARCH_DEFAULTS.GROUP.K
): Promise<VideoCategoryGroup[]> => {
  if (!categoryEmbedding || categoryEmbedding.length === 0) {
    return [];
  }

  // Use multi-search endpoint (POST) to handle large embedding vectors
  // When distance_threshold < 1.0, omit k parameter to let threshold do the filtering
  // When distance_threshold >= 1.0 (no filtering), use k to limit results
  const vectorParams =
    distanceThreshold < 1.0
      ? `distance_threshold:${distanceThreshold}`
      : `k:${k}`;

  const multiSearchResponse = await context.client.multiSearch.perform(
    {
      searches: [
        {
          collection: STOCK_VIDEO_CATEGORY_GROUPS,
          q: '*',
          vector_query: `embedding:([${categoryEmbedding.join(',')}], ${vectorParams})`,
          per_page: 5,
          prefix: false,
          sort_by: '_vector_distance:asc',
          include_fields: VIDEO_CATEGORY_GROUP_FIELDS,
        },
      ],
    },
    {},
    { cacheSearchResultsForSeconds: 60 }
  );

  const result = multiSearchResponse.results[0] as {
    hits?: Array<{
      document: VideoCategoryGroup;
    }>;
  };
  return result.hits?.map((hit) => hit.document) ?? [];
};
